import { API } from '@/lib/api';
import { useState } from "react";
import { getErrorMessage } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Truck, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";



const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  const [error, setError] = useState("");

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.email || !formData.password || (isRegister && !formData.name)) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const endpoint = isRegister ? `${API}/auth/admin/register` : `${API}/auth/admin/login`;
      const payload = isRegister 
        ? formData 
        : { email: formData.email, password: formData.password };
      
      const response = await axios.post(endpoint, payload);
      
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_name", response.data.admin_name);
      localStorage.setItem("admin_email", response.data.admin_email);
      
      toast.success(isRegister ? "Account created successfully!" : "Welcome back!");
      navigate("/admin/dashboard");
    } catch (error) {
      const message = getErrorMessage(error, "Authentication failed");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="absolute inset-0 noise-overlay"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_deliverstack/artifacts/2v9gvns2_EB4BFD0D-4C9D-43F2-B0D8-4B4D0DF14E28_1_201_a.jpeg" 
            alt="Stack Logo" 
            className="h-16 w-auto rounded-2xl mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">Stack Admin</h1>
          <p className="text-stone-400 mt-2">
            {isRegister ? "Create your admin account" : "Sign in to your dashboard"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-card p-8" data-testid="admin-login-form">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-5">
            {isRegister && (
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">Full Name</Label>
                <Input
                  className="input-style"
                  placeholder="Admin Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  data-testid="admin-name-input"
                />
              </div>
            )}

            <div>
              <Label className="text-emerald-950 font-medium mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  className="input-style pl-10"
                  type="email"
                  placeholder="admin@stack.ca"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-emerald-950 font-medium mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  className="input-style pl-10"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="admin-submit-btn"
            >
              {loading ? "Please wait..." : (isRegister ? "Create Account" : "Sign In")}
            </Button>
          </div>
        </form>

        {/* Toggle */}
        <p className="text-center text-stone-400 mt-6">
          {isRegister ? "Already have an account?" : "Need an admin account?"}{" "}
          <button
            onClick={() => {
              setIsRegister(!isRegister);
              setError("");
            }}
            className="text-lime-400 hover:underline font-medium"
            data-testid="toggle-auth-btn"
          >
            {isRegister ? "Sign In" : "Register"}
          </button>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
